import { useCallback, useEffect, useMemo, useState } from 'react';
import { MdSearch } from 'react-icons/md';
import PageHeader from '../../../components/owner/PageHeader';
import {
  exportUserCsv,
  exportUserExcel,
  exportUserPdf,
  exportUsers,
  printUserList,
} from './exportUsers';
import UserDetailModal from './UserDetailModal';
import {
  fetchGymAdminList,
  fetchGymFilterOptions,
  updateGymAdminEmail,
  updateGymAdminStatus,
} from '../../../services/owner/gymAdminsApi';
import { peekOwnerCache } from '../../../services/cache/ownerCache';
import { usePanelToast } from '../../../context/PanelToastContext';
import '../ownerPages.css';
import '../subscriptions/subscriptions.css';
import './users.css';

function StatusBadge({ status }) {
  const isActive = status === 'active';
  return (
    <span className={`owner-badge ${isActive ? 'owner-badge-success' : 'owner-badge-warning'}`}>
      {isActive ? 'Active' : 'Suspended'}
    </span>
  );
}

function UserManagement() {
  const toast = usePanelToast();
  const cachedAdmins = peekOwnerCache('gym-admins');
  const cachedGyms = peekOwnerCache('gym-filter-options');
  const [admins, setAdmins] = useState(cachedAdmins || []);
  const [gymOptions, setGymOptions] = useState(cachedGyms || ['All Gyms']);
  const [loading, setLoading] = useState(!cachedAdmins);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [gymFilter, setGymFilter] = useState('All Gyms');
  const [confirmAction, setConfirmAction] = useState(null);
  const [viewUser, setViewUser] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  const loadData = useCallback(async () => {
    try {
      setError('');
      if (!peekOwnerCache('gym-admins')) setLoading(true);
      const [adminList, gyms] = await Promise.all([
        fetchGymAdminList(),
        fetchGymFilterOptions(),
      ]);
      setAdmins(adminList);
      setGymOptions(gyms);
    } catch (err) {
      setError(err.message || 'Failed to load gym admins');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const filteredAdmins = useMemo(() => {
    const query = search.trim().toLowerCase();

    return admins.filter((admin) => {
      const matchesSearch =
        !query ||
        admin.name.toLowerCase().includes(query) ||
        admin.email.toLowerCase().includes(query) ||
        admin.gym.toLowerCase().includes(query);

      const matchesStatus =
        statusFilter === 'all' || admin.status === statusFilter;

      const matchesGym =
        gymFilter === 'All Gyms' || admin.gym === gymFilter;

      return matchesSearch && matchesStatus && matchesGym;
    });
  }, [admins, search, statusFilter, gymFilter]);

  const handleClearFilters = () => {
    setSearch('');
    setStatusFilter('all');
    setGymFilter('All Gyms');
  };

  const handleSuspend = (admin) => {
    setConfirmAction({ type: 'suspend', admin });
  };

  const handleActivate = (admin) => {
    setConfirmAction({ type: 'activate', admin });
  };

  const handleViewUser = (admin) => {
    setViewUser(admin);
  };

  const handleEmailSaved = async (admin, nextEmail) => {
    const updated = await updateGymAdminEmail(admin, nextEmail);
    setAdmins((prev) => prev.map((a) => (a.id === admin.id ? { ...a, ...updated } : a)));
    setViewUser((prev) => (prev && prev.id === admin.id ? { ...prev, ...updated } : prev));
    toast.showUpdated('Admin email');
    return updated;
  };

  const closeConfirm = () => setConfirmAction(null);

  const executeAction = async () => {
    if (!confirmAction) return;

    const { type, admin } = confirmAction;

    try {
      setActionLoading(true);
      const nextStatus = type === 'suspend' ? 'suspended' : 'active';
      const updated = await updateGymAdminStatus(admin, nextStatus);
      setAdmins((prev) => prev.map((a) => (a.id === admin.id ? updated : a)));
      toast.showUpdated('Gym admin');
      closeConfirm();
    } catch (err) {
      toast.showError(err.message || 'Action failed');
    } finally {
      setActionLoading(false);
    }
  };

  const getConfirmContent = () => {
    if (!confirmAction) return { title: '', message: '', confirmLabel: '' };

    const { type, admin } = confirmAction;

    if (type === 'suspend') {
      return {
        title: 'Suspend Account',
        message: (
          <>
            Suspend the account for <strong>{admin.name}</strong> at{' '}
            <strong>{admin.gym}</strong>? They will lose access immediately.
          </>
        ),
        confirmLabel: 'Suspend Account',
        danger: true,
      };
    }
    return {
      title: 'Activate Account',
      message: (
        <>
          Reactivate the account for <strong>{admin.name}</strong> at{' '}
          <strong>{admin.gym}</strong>?
        </>
      ),
      confirmLabel: 'Activate Account',
    };
  };

  const confirmContent = getConfirmContent();

  return (
    <div>
      <PageHeader
        title="Gym Admins"
        subtitle="Manage gym admin accounts across all gyms"
      />

      {loading && <p className="owner-placeholder-text owner-mb-16">Loading gym admins...</p>}
      {error && <p className="owner-placeholder-text owner-mb-16" style={{ color: 'var(--owner-danger, #dc2626)' }}>{error}</p>}

      <div className="rec-member-toolbar admin-list-toolbar">
        <div className="rec-member-search-field">
          <MdSearch className="rec-member-search-icon" />
          <input
            type="text"
            className="owner-form-input rec-member-search-input"
            placeholder="Search by name, email, or gym..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <select
          className="owner-form-input rec-member-toolbar-select"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          aria-label="Filter by status"
        >
          <option value="all">All Status</option>
          <option value="active">Active</option>
          <option value="suspended">Suspended</option>
        </select>
        <select
          className="owner-form-input rec-member-toolbar-select"
          value={gymFilter}
          onChange={(e) => setGymFilter(e.target.value)}
          aria-label="Filter by gym"
        >
          {gymOptions.map((gym) => (
            <option key={gym} value={gym}>{gym}</option>
          ))}
        </select>
        <div className="rec-member-toolbar-actions">
          <button type="button" className="owner-btn owner-btn-secondary owner-btn-sm" onClick={handleClearFilters}>
            Clear Filters
          </button>
          <button type="button" className="owner-btn owner-btn-secondary owner-btn-sm" onClick={() => printUserList(filteredAdmins)}>
            Print
          </button>
          <button type="button" className="owner-btn owner-btn-secondary owner-btn-sm" onClick={() => exportUserCsv(filteredAdmins)}>
            Export CSV
          </button>
          <button type="button" className="owner-btn owner-btn-secondary owner-btn-sm" onClick={() => exportUserPdf(filteredAdmins)}>
            Export PDF
          </button>
          <button type="button" className="owner-btn owner-btn-secondary owner-btn-sm" onClick={() => exportUserExcel(filteredAdmins)}>
            Export Excel
          </button>
          <button type="button" className="owner-btn owner-btn-primary owner-btn-sm" onClick={() => exportUsers(filteredAdmins)}>
            Export
          </button>
        </div>
      </div>

      <div className="owner-card rec-panel-card owner-panel-section">
        <div className="owner-table-wrap">
          <table className="owner-table rec-member-table admin-list-table owner-panel-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Gym</th>
                <th>Joined</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredAdmins.length > 0 ? (
                filteredAdmins.map((admin) => (
                  <tr key={admin.id}>
                    <td>{admin.name}</td>
                    <td>{admin.email}</td>
                    <td>{admin.gym}</td>
                    <td>{admin.joinedDate}</td>
                    <td><StatusBadge status={admin.status} /></td>
                    <td>
                      <div className="user-actions-cell">
                        <button
                          type="button"
                          className="owner-btn owner-btn-primary owner-btn-sm"
                          onClick={() => handleViewUser(admin)}
                        >
                          View User
                        </button>
                        {admin.status === 'active' ? (
                          <button
                            type="button"
                            className="owner-btn owner-btn-sm user-btn-danger"
                            onClick={() => handleSuspend(admin)}
                          >
                            Suspend Account
                          </button>
                        ) : (
                          <button
                            type="button"
                            className="owner-btn owner-btn-primary owner-btn-sm"
                            onClick={() => handleActivate(admin)}
                          >
                            Activate Account
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={6} className="user-empty-state">
                    {loading ? 'Loading...' : 'No gym admins found. Admins appear here after gym registration or when added in Supabase.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {confirmAction && (
        <div className="subscription-modal-overlay" onClick={closeConfirm}>
          <div
            className="subscription-modal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            <div className="subscription-modal-header">
              <h2 className="subscription-modal-title">{confirmContent.title}</h2>
              <button
                type="button"
                className="subscription-modal-close"
                onClick={closeConfirm}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <p className="user-modal-message">{confirmContent.message}</p>
            <div className="subscription-form-actions">
              <button
                type="button"
                className={`owner-btn ${confirmContent.danger ? 'user-btn-danger' : 'owner-btn-primary'}`}
                onClick={executeAction}
                disabled={actionLoading}
              >
                {actionLoading ? 'Saving…' : confirmContent.confirmLabel}
              </button>
              <button
                type="button"
                className="owner-btn owner-btn-secondary"
                onClick={closeConfirm}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {viewUser && (
        <UserDetailModal
          user={viewUser}
          onClose={() => setViewUser(null)}
          onEmailSaved={handleEmailSaved}
        />
      )}
    </div>
  );
}

export default UserManagement;
